<script lang="ts" setup>
import { computed, ref } from 'vue';
import FolderListGroup from '@/components/sidebar/FolderListGroup.vue';
import useFoldersService from '@/composables/apiServices/useFoldersService';
import type { IHashedFolderItem } from '@/types/services/folder.service';
import { ROUTE_NAMES } from '@/router/conf';
import { useRoute, useRouter } from 'vue-router';
import EditFolder from '@/components/settings/folders/EditFolder.vue';
import { FolderEditActionType } from '@/types/components/settings/folders/Consts';
import useDisabledFeature from '@/composables/useDisabledFeature';
import { EDisabledFeatures } from '@/types/composables/useDisabledFeatureEnums';
import useMailboxSharedProvides from '@/composables/mailbox/useMailboxSharedProvides';
import { useI18n } from 'vue-i18n';
import useFolderListProvides from '@/composables/useFolderListProvides';
import useUserService from '@/composables/apiServices/useUserService';

const { t } = useI18n();
const router = useRouter();
const route = useRoute();

const folderLevel = ref(1);

const { attributes } = useUserService();
const { data: userAttributesResponse, suspense: userAttributesSuspense } = attributes();

const { getFolderTree } = useFoldersService();
const { data: folderTreeResponse, suspense: folderTreeSuspense } = getFolderTree();

const { provideMailboxFlatFolders } = useMailboxSharedProvides();
const { flatFoldersSuspense } = provideMailboxFlatFolders();

const { provideFolderListOpened, provideFolderListDraggedOver } = useFolderListProvides();

const { openedFolders } = provideFolderListOpened();
provideFolderListDraggedOver();

const { isFeatureDisabled } = useDisabledFeature();

const [isFoldersDisabled] = await Promise.all([
  isFeatureDisabled(EDisabledFeatures.FOLDERS),
  folderTreeSuspense(),
  flatFoldersSuspense(),
  userAttributesSuspense()
]);

const filteredFolderTreeByUnreadToggle = computed(() => {
  const filterUnreadFolders = (
    folders: IHashedFolderItem[],
    parents: IHashedFolderItem[]
  ): IHashedFolderItem[] => {
    return folders
      .map((folder) => {
        const filteredChildren = filterUnreadFolders(folder.children, [...parents, folder]);
        const isCurrentFolder = folder.id === route.params.mailboxId;
        const hasUnseenMails = folder.unseen > 0 || filteredChildren.length > 0;
        const unseenMailCount =
          folder.unseen +
          (!openedFolders.value[folder.id] ||
          parents.some((parent) => !openedFolders.value[parent.id])
            ? filteredChildren.reduce((acc, curr) => acc + curr.unseen, 0)
            : 0);

        if (
          isCurrentFolder ||
          hasUnseenMails ||
          !userAttributesResponse.value?.unreadFolderFilterToggled
        ) {
          return {
            ...folder,
            unseen: unseenMailCount,
            children: filteredChildren
          };
        }
        return null;
      })
      .filter((folder): folder is IHashedFolderItem => folder !== null);
  };

  return filterUnreadFolders(folderTreeResponse.value?.data || [], []);
});

const computedSystemNonSystemFolders = computed(() => {
  return (
    filteredFolderTreeByUnreadToggle.value.reduce(
      (acc, curr) => {
        if (curr.systemFolder) {
          acc.systemFolders.push(curr);
        } else {
          acc.nonSystemFolders.push(curr);
        }
        return acc;
      },
      { systemFolders: [] as IHashedFolderItem[], nonSystemFolders: [] as IHashedFolderItem[] }
    ) || { systemFolders: [], nonSystemFolders: [] }
  );
});

const handleFolderSettings = async () => {
  await router.push({
    name: ROUTE_NAMES.SETTINGS_FOLDERS,
    params: {
      userIndex: route.params.userIndex
    }
  });
};
</script>

<template>
  <div
    data-onboarding="folder-list"
    :class="[
      'folder-list',
      'pt-md-4',
      'overflow-auto',
      `folder-list--${userAttributesResponse?.folderListSize || 'default'}`
    ]"
  >
    <FolderListGroup
      v-if="computedSystemNonSystemFolders.systemFolders"
      :count="folderLevel"
      :items="computedSystemNonSystemFolders.systemFolders"
    />
    <div v-if="!isFoldersDisabled" class="folder-list__actions">
      <EditFolder :action-type="FolderEditActionType.NEW" />

      <v-tooltip>
        <template #activator="{ props: tooltipProps }">
          <TextBtn
            data-test-id="sidebar-redirect-settings"
            v-bind="tooltipProps"
            @click="handleFolderSettings"
            sentry-unmask
          >
            <VIcon color="alert-close" size="large">mdi-cog-outline</VIcon>
          </TextBtn>
        </template>
        <span>{{ t('sidebar.folders.toSettings') }}</span>
      </v-tooltip>
    </div>

    <FolderListGroup
      v-if="computedSystemNonSystemFolders.nonSystemFolders"
      :count="folderLevel"
      :items="computedSystemNonSystemFolders.nonSystemFolders"
    />
  </div>
</template>

<style lang="scss" scoped>
@use '../../assets/styles/settings' as settings;
@use 'sass:map';

.folder-list {
  color: rgb(var(--v-theme-alert-close));

  &:deep(.v-list-item__prepend .v-list-item__spacer) {
    width: 0.5rem;

    @media screen and #{map.get(settings.$display-breakpoints, 'sm-and-up')} {
      width: 0.75rem;
    }
  }

  &--with-overlay {
    z-index: 1009;
  }

  --zwm-folder-list-item-font-size: 0.9375rem;
  --zwm-folder-list-item-padding-y: 0.375rem;
  --zwm-folder-list-item-padding-x: 0.5rem;

  &__actions {
    :deep(button) {
      --v-btn-height: 2.25rem;
      --v-btn-size: var(--zwm-folder-list-item-font-size);

      min-width: 0;
      padding: 0 0.375rem;
    }
  }

  &--comfortable {
    --zwm-folder-list-item-font-size: 0.875rem;
    --zwm-folder-list-item-padding-y: 0.25rem;
    --zwm-folder-list-item-padding-x: 0.5rem;

    .folder-list__actions {
      :deep(button) {
        --v-btn-height: 2rem;

        padding: 0 0.25rem;
      }
    }
  }

  &--compact {
    --zwm-folder-list-item-font-size: 0.875rem;
    --zwm-folder-list-item-padding-y: 0;
    --zwm-folder-list-item-padding-x: 0.5rem;

    .folder-list__actions {
      :deep(button) {
        --v-btn-height: 1.5rem;

        padding: 0 0.25rem;
      }
    }
  }

  &__actions {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    padding-left: 0.5rem;
    padding-right: 1rem;
  }
}
</style>
